// client/src/lib/api/shifts-api.ts
// ===========================================
// MÓDULO DE GESTIÓN DE CAJA Y TURNOS (SHIFTS)
// Maneja registros de caja, apertura/cierre de turnos, transacciones y discrepancias.
// ===========================================

import { apiRequest, buildUrlWithParams, ApiError } from "./api-client";

// -------------------------------------------
// --- CASH REGISTERS ---
// -------------------------------------------

export async function getShopCashRegisters(shopId: string): Promise<any[]> {
  const response = await apiRequest<{ cashRegisters: any[] }>(
    `/api/shops/${shopId}/cash-registers`,
  );
  return response.cashRegisters;
}

export async function createCashRegister(registerData: {
  shopId: string;
  name: string;
  location?: string;
  description?: string;
}): Promise<any> {
  const response = await apiRequest<{ cashRegister: any }>(
    `/api/shops/${registerData.shopId}/cash-registers`,
    {
      method: "POST",
      body: JSON.stringify(registerData),
    },
  );
  return response.cashRegister;
}

export async function updateCashRegister(
  shopId: string,
  registerId: string,
  updates: {
    name?: string;
    location?: string;
    description?: string;
    isActive?: boolean;
  },
): Promise<any> {
  const response = await apiRequest<{ cashRegister: any }>(
    `/api/shops/${shopId}/cash-registers/${registerId}`,
    {
      method: "PATCH",
      body: JSON.stringify(updates),
    },
  );
  return response.cashRegister;
}

export async function deleteCashRegister(
  shopId: string,
  registerId: string,
): Promise<void> {
  return apiRequest<void>(`/api/shops/${shopId}/cash-registers/${registerId}`, {
    method: "DELETE",
  });
}

// -------------------------------------------
// --- SHIFTS (TURNOS) ---
// -------------------------------------------

// COMENTARIO: Usamos buildUrlWithParams para manejar el userId opcional limpiamente.
// Mantenemos la lógica original: si es 404 (no hay turno), retornamos null.
export async function getCurrentShift(
  shopId: string,
  userId?: string,
): Promise<any> {
  try {
    const url = buildUrlWithParams(`/api/shops/${shopId}/shifts/current`, {
      userId,
    });
    const response = await apiRequest<{ shift: any }>(url);
    return response.shift;
  } catch (error) {
    // Si el backend devuelve 404 indicando que no hay turno activo, retornamos null en vez de error
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function openCashierShift(shiftData: {
  shopId: string;
  cashRegisterId: string;
  employeeId: string;
  initialCash: string;
  openedBy: string;
}): Promise<any> {
  const response = await apiRequest<{ shift: any }>("/api/shifts/open", {
    method: "POST",
    body: JSON.stringify(shiftData),
  });
  return response.shift;
}

// COMENTARIO: En el código original, esta función inyectaba manualmente x-shop-id y x-user-id.
// Como apiRequest ya inyecta estos headers automáticamente (basado en getCurrentUser/Shop),
// hemos eliminado esa lógica redundante para hacer el código más limpio.
export async function closeCashierShift(
  shiftId: string,
  closeData: {
    countedCash: string;
    notes?: string;
    closedBy: string;
    cashDifference?: string;
    discrepancyType?: string;
    denominationBreakdown?: Record<string, number>;
    authorizationCode?: string;
  },
): Promise<any> {
  try {
    const response = await apiRequest<{ shift: any }>(
      `/api/shifts/${shiftId}/close`,
      {
        method: "POST",
        body: JSON.stringify(closeData),
      },
    );
    return response.shift;
  } catch (error: any) {
    // {Preservar details del backend en el mensaje}
    const details = error?.data?.details || "";
    const message = error?.message || "Error al cerrar el turno";
    // {Re-lanzar con el mensaje completo}
    throw new Error(details ? details : message);
  }
}

export async function getShopShifts(
  shopId: string,
  filters?: {
    startDate?: string;
    endDate?: string;
    status?: string;
    employeeId?: string;
  },
): Promise<any[]> {
  const url = buildUrlWithParams(`/api/shops/${shopId}/shifts`, filters);
  const response = await apiRequest<{ shifts: any[] }>(url);
  return response.shifts;
}

export async function getShiftTransactions(shiftId: string): Promise<any[]> {
  const response = await apiRequest<{ transactions: any[] }>(
    `/api/shifts/${shiftId}/transactions`,
  );
  return response.transactions;
}

export async function getShiftReport(shiftId: string): Promise<any> {
  const response = await apiRequest<{ report: any }>(
    `/api/shifts/${shiftId}/report`,
  );
  return response.report;
}

export async function createShiftTransaction(transactionData: {
  shiftId: string;
  type: "sale" | "refund" | "expense" | "cash_in" | "cash_out";
  amount: string;
  paymentMethod: "cash" | "card" | "transfer" | "mixed";
  description?: string;
  createdBy: string;
  saleId?: string;
}): Promise<any> {
  const response = await apiRequest<{ transaction: any }>(
    "/api/shifts/transactions",
    {
      method: "POST",
      body: JSON.stringify(transactionData),
    },
  );
  return response.transaction;
}

// -------------------------------------------
// --- DISCREPANCIES (OWNER) ---
// -------------------------------------------

export async function getOwnerDiscrepancies(ownerId: string): Promise<{
  discrepancies: Array<{
    id: string;
    shopId: string;
    shopName: string;
    shiftId: string;
    cashRegisterName: string;
    expectedCash: string;
    countedCash: string;
    cashDifference: string;
    discrepancyType: "faltante" | "sobrante";
    notes?: string;
    closedBy: string;
    createdAt: string;
  }>;
  count: number;
  message: string;
  success: boolean;
}> {
  return apiRequest(`/api/owners/${ownerId}/discrepancies`);
}
